import { useTranslation } from 'react-i18next'

/**
 * FilterBar — shared utilities and sub-components for all filter panels.
 *
 * Usage:
 *   import { filterFieldCls, filterLabelCls, filterCardCls, FilterDateGroup }
 *     from '@/components/common/FilterBar'
 */

// ── Style helpers ─────────────────────────────────────────────────────────────

/**
 * Returns full CSS classes for a filter <input> or <select>.
 * Pass `isActive=true` when the field has a user-set value to show the
 * sky-blue active highlight (border + subtle ring + bg tint).
 */
export function filterFieldCls(dark: boolean, isActive = false): string {
  const base = 'text-[12px] px-2.5 py-1.5 rounded-lg border outline-none transition-colors w-full'
  if (isActive) {
    return `${base} ${dark
      ? 'bg-slate-800 border-sky-500/60 text-slate-100 placeholder-slate-500 ring-1 ring-sky-500/15'
      : 'bg-sky-50/60 border-sky-400 text-slate-800 placeholder-slate-400 ring-1 ring-sky-300/25'
    }`
  }
  return `${base} ${dark
    ? 'bg-slate-800 border-white/10 text-slate-100 placeholder-slate-500 focus:border-sky-500'
    : 'bg-white border-slate-200 text-slate-800 placeholder-slate-400 focus:border-sky-400'
  }`
}

/** Returns CSS classes for a filter field label (uppercase, tracking). */
export function filterLabelCls(dark: boolean): string {
  return `text-[11px] font-semibold uppercase tracking-wider ${dark ? 'text-slate-400' : 'text-slate-600'}`
}

/** Returns CSS classes for the filter panel card wrapper. */
export function filterCardCls(dark: boolean): string {
  return `rounded-xl border shadow-sm ${dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'}`
}

// ── FilterDateGroup ───────────────────────────────────────────────────────────

/**
 * Visually groups Desde / Hasta date inputs as a single unit.
 * Both inputs share one outer border; a thin divider separates them.
 * The group gets the sky active highlight when either input has a value.
 */
export function FilterDateGroup({
  dark,
  desde,
  hasta,
  onDesde,
  onHasta,
}: {
  dark: boolean
  desde: string
  hasta: string
  onDesde: (v: string) => void
  onHasta: (v: string) => void
}) {
  const { t } = useTranslation()

  const hasValue = !!desde || !!hasta

  const groupBg     = dark ? 'bg-slate-800' : 'bg-white'
  const groupBorder = hasValue
    ? (dark
        ? 'border-sky-500/60 ring-1 ring-sky-500/15'
        : 'border-sky-400 ring-1 ring-sky-300/25')
    : (dark
        ? 'border-white/10 focus-within:border-sky-500/60'
        : 'border-slate-200 focus-within:border-sky-400')
  const divider = dark ? 'bg-white/10' : 'bg-slate-200'
  const inputBase = [
    'text-[12px] px-2.5 py-1.5 bg-transparent outline-none min-w-0',
    dark ? 'text-slate-100 placeholder-slate-500' : 'text-slate-800 placeholder-slate-400',
  ].join(' ')

  return (
    <div className="flex flex-col gap-1">
      {/* Shared label row */}
      <div className={`flex items-center gap-2 ${filterLabelCls(dark)}`}>
        <span>{t('common.from')}</span>
        <span className="opacity-30">–</span>
        <span>{t('common.to')}</span>
      </div>
      {/* Single-border group */}
      <div className={`flex items-center rounded-lg border overflow-hidden transition-all ${groupBg} ${groupBorder}`}>
        <input
          type="date"
          value={desde}
          onChange={e => onDesde(e.target.value)}
          className={inputBase}
        />
        <div className={`w-px self-stretch shrink-0 ${divider}`} aria-hidden="true" />
        <input
          type="date"
          value={hasta}
          onChange={e => onHasta(e.target.value)}
          className={inputBase}
        />
      </div>
    </div>
  )
}
